// Call state and recordings (api-phase1-telephony-audit.md): the live state line of a call row
// (dialing → ringing → connected with a running talk timer → ended with talk time) and the compact
// recording player used in the Contacts tab, the Log and the audit list.

import { useEffect, useState } from 'react';
import { api, errorText, fetchBlobUrl, type CallRecording, type CallResult, type EventAction } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { useNow } from '../hooks.ts';
import { dur, human, ms } from './util.ts';

function Ticker({ from }: { from: number }) {
  const now = useNow(1000);
  return <>{dur(now - from)}</>;
}

/** One line: what the call is doing now, with a running timer while it is live. */
export function CallStateLine({ a, r }: { a: EventAction; r: CallResult }) {
  const t0 = ms(a.startedAt) ?? Date.now();
  switch (r.state) {
    case 'dialing':
      return (
        <span className="cstate live">
          <Icon name="phone" />
          {r.provider === 'manual' ? 'Calling' : 'Dialing…'} <Ticker from={t0} />
        </span>
      );
    case 'ringing':
      return (
        <span className="cstate live">
          <Icon name="phone" />
          Ringing… <Ticker from={ms(r.ringingAt) ?? t0} />
        </span>
      );
    case 'connected':
      return (
        <span className="cstate on" title="Talk time">
          <Icon name="phone" />
          Connected · <b className="mono">{<Ticker from={ms(r.connectedAt) ?? t0} />}</b>
        </span>
      );
    case 'ended':
      return (
        <span className="cstate end">
          Ended
          {r.talkSeconds !== null ? ` · talk ${dur(r.talkSeconds * 1000)}` : ''}
          {r.endReason && r.endReason !== 'hangup' ? ` · ${human(r.endReason)}` : ''}
        </span>
      );
    case 'failed':
      return <span className="cstate bad">Failed{r.endReason ? ` · ${human(r.endReason)}` : ''}</span>;
  }
}

/**
 * The call's recording: `stored` → play button that fetches the audio with auth headers into a blob URL
 * (each fetch is audited as call.recording_played, so nothing is fetched until the user asks), the
 * duration and the first 12 characters of the SHA-256. `pending` and `missing` show their status.
 */
export function RecordingView({ eventId, actionId, rec }: { eventId: string; actionId: string; rec: CallRecording }) {
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(
    () => () => {
      if (src) URL.revokeObjectURL(src);
    },
    [src],
  );

  if (rec.status === 'pending') {
    return (
      <div className="rec" role="status">
        <span className="spinner" aria-hidden="true" />
        <span className="muted">Recording processing…</span>
      </div>
    );
  }
  if (rec.status === 'missing') {
    return (
      <div className="rec missing" role="alert">
        <Icon name="alert" />
        Recording missing — supervisor notified
      </div>
    );
  }
  if (rec.status === 'not_connected') {
    return <div className="rec dim">No recording · the call did not connect</div>;
  }

  const play = async () => {
    setBusy(true);
    setErr(null);
    try {
      setSrc(await fetchBlobUrl(rec.url ?? api.callRecordingUrl(eventId, actionId), { cache: false }));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  const meta = (
    <>
      {rec.durationSeconds !== null && <span className="mono">{dur(rec.durationSeconds * 1000)}</span>}
      {rec.sha256 && (
        <span className="mono dim" title={`SHA-256 ${rec.sha256}`}>
          SHA-256 {rec.sha256.slice(0, 12)}
        </span>
      )}
    </>
  );
  return (
    <div className="rec">
      {src ? (
        <audio controls autoPlay src={src} aria-label="Call recording" />
      ) : (
        <button type="button" className="btn sm" onClick={() => void play()} disabled={busy} title="Play the call recording (audited)">
          <Icon name="play" />
          {busy ? 'Loading…' : 'Recording'}
        </button>
      )}
      {meta}
      {err ? <span className="bad-t small">{errorText(err)}</span> : null}
    </div>
  );
}
